import { CheckCircle2, PlugZap, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { PortalPingResponseType } from '@/types/types_portal';
import { callableErrorMessage, pingPortal } from '@/util/callable_functions';

/**
 * Checks that this project can still call /portal's internal API.
 *
 * The connection is machine-to-machine — our functions authenticate to theirs
 * with an identity token minted from this project's own service account, and
 * /portal grants that account per endpoint. Nothing in the browser can test
 * that, and nothing tells us when it breaks: a service account rotated on their
 * side, or an endpoint redeployed without ours in its invoker list, only shows
 * up the next time a real call fails. This is the button that asks.
 *
 * `internal_ping` reads and writes nothing on either side, so pressing it is
 * always safe.
 */
export function PortalPingCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PortalPingResponseType | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handlePing() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const response = await pingPortal();
      setResult(response.data);
    } catch (e) {
      console.error('pingPortal failed', e);
      // Shown verbatim: the function passes Cloud Run's own refusal through, and
      // "403" (they have not granted us) and "401" (they rejected the token) are
      // fixed in different places.
      setError(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
      <div className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4'>
        <div className='tw:flex tw:items-center tw:gap-3'>
          <PlugZap className='tw:size-5 tw:text-primary' />
          <div>
            <p className='tw:font-medium'>Portal connection</p>
            <p className='tw:text-sm tw:text-muted-foreground'>
              Check that this site can still reach the Eighty Days portal. Reads nothing and changes nothing.
            </p>
          </div>
        </div>
        <Button variant='outline' onClick={handlePing} loading={busy}>
          {busy ? 'Checking…' : 'Check connection'}
        </Button>
      </div>

      {result && (
        <div className='tw:mt-4 tw:flex tw:items-start tw:gap-3 tw:rounded tw:border tw:border-green-700/30 tw:bg-green-700/5 tw:px-4 tw:py-3 tw:text-sm'>
          <CheckCircle2 className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-green-700' />
          <div>
            <p className='tw:font-medium tw:text-green-700'>
              Connected to the portal{result.environment ? ` (${result.environment})` : ''}.
            </p>
            {/* The service account the portal saw, which is what has to appear in
                their invoker list — worth reading off rather than describing. */}
            <p className='tw:mt-1 tw:text-muted-foreground'>
              They know us as <code className='tw:text-xs'>{result.caller}</code>, at{' '}
              {new Date(result.now).toLocaleString()}.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className='tw:mt-4 tw:flex tw:items-start tw:gap-3 tw:rounded tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-4 tw:py-3 tw:text-sm tw:text-ed-red'>
          <XCircle className='tw:mt-0.5 tw:size-4 tw:shrink-0' />
          <p>{error}</p>
        </div>
      )}
    </section>
  );
}
